import React, { useCallback, useEffect, useRef, useState } from 'react'
import { apiGet, apiPost } from '../../utils/api'
import { useSocket } from '../../contexts/SocketContext'
import { useWhatsAppStatus } from '../../contexts/WhatsAppStatusContext'
import { getWaGroups, linkWaGroup, type WaGroup } from './crmApi'
import { crmToast, SearchInput } from './crmUi'

/**
 * WhatsApp tab on a lead (Lee, 6 Aug 2026) — the lead's linked LIVE group as a full
 * working chat, same data and endpoints as the support side's Groups page: read the
 * conversation, send text / images / videos / documents / voice notes, reply, star.
 * Shares the SAME link (deal.whatsapp_group_jid) as Conversation History — link once,
 * both tabs use it. Live via the whatsapp_group_message socket + 5s polling.
 */

interface GroupMessage {
  id: string
  chatId: string
  from: string
  body?: string
  type: string
  timestamp?: number
  direction: 'INCOMING' | 'OUTGOING'
  metadata?: any
  mediaUrl?: string
  senderName?: string
  isStarred?: boolean
  replyToId?: string
  replyBody?: string
  replySenderName?: string
  replyType?: string
}

const SENDER_COLORS = ['#e06c75', '#06cf9c', '#53bdeb', '#e9a032', '#7c6bff', '#f06292', '#26c6da', '#ff7043']
const senderColor = (id: string) => {
  let h = 0
  for (let i = 0; i < id.length; i++) h = id.charCodeAt(i) + ((h << 5) - h)
  return SENDER_COLORS[Math.abs(h) % SENDER_COLORS.length]
}

const AVATAR_COLORS = ['#25D366', '#34B7F1', '#E91E63', '#9C27B0', '#FF9800', '#00BCD4', '#FF5722', '#3F51B5']
const avatarColor = (name: string) => {
  let h = 0
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h)
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
}
const initials = (name: string) => {
  const w = name.trim().split(/\s+/)
  return (w.length >= 2 ? w[0][0] + w[1][0] : name.slice(0, 2)).toUpperCase()
}

const fmtTime = (ts?: number) => (ts ? new Date(ts * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '')
const fmtDate = (ts?: number) => {
  if (!ts) return ''
  const d = new Date(ts * 1000)
  const today = new Date()
  if (d.toDateString() === today.toDateString()) return 'Today'
  const y = new Date(today)
  y.setDate(y.getDate() - 1)
  if (d.toDateString() === y.toDateString()) return 'Yesterday'
  return d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })
}

const linkify = (text: string): React.ReactNode => {
  const rx = /(https?:\/\/[^\s<>]+|www\.[^\s<>]+)/gi
  const parts = text.split(rx)
  if (parts.length === 1) return text
  return parts.map((part, i) => {
    if (/^https?:\/\//i.test(part) || /^www\./i.test(part)) {
      const href = (part.startsWith('www.') ? `https://${part}` : part).replace(/[)}\].,;:!?]+$/, '')
      const label = part.replace(/[)}\].,;:!?]+$/, '')
      return (
        <React.Fragment key={i}>
          <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline break-all" onClick={(e) => e.stopPropagation()}>{label}</a>
          {part.slice(label.length)}
        </React.Fragment>
      )
    }
    return <React.Fragment key={i}>{part}</React.Fragment>
  })
}

const metaOf = (msg: GroupMessage): any => {
  if (!msg.metadata) return {}
  if (typeof msg.metadata === 'string') { try { return JSON.parse(msg.metadata) } catch { return {} } }
  return msg.metadata
}

export const DealWhatsApp: React.FC<{ dealId: string; dealName?: string }> = ({ dealId, dealName }) => {
  const { socket } = useSocket()
  const waStatus = useWhatsAppStatus()
  const sessionId = waStatus.activeSession?.id || null

  const [link, setLink] = useState<{ jid: string | null; name: string | null } | null>(null)
  const [groups, setGroups] = useState<WaGroup[] | null>(null)
  const [gatewayUp, setGatewayUp] = useState(true)
  const [search, setSearch] = useState('')
  const [pictureUrl, setPictureUrl] = useState<string | null>(null)
  const [participants, setParticipants] = useState<number | null>(null)
  const [messages, setMessages] = useState<GroupMessage[]>([])
  const [loadingMsgs, setLoadingMsgs] = useState(false)
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [replyTo, setReplyTo] = useState<GroupMessage | null>(null)
  const [pendingFile, setPendingFile] = useState<{ name: string; size: number; type: string; preview?: string; base64: string } | null>(null)
  const [menu, setMenu] = useState<{ x: number; y: number; msg: GroupMessage } | null>(null)
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [recording, setRecording] = useState(false)
  const [recSecs, setRecSecs] = useState(0)

  const endRef = useRef<HTMLDivElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const recRef = useRef<MediaRecorder | null>(null)
  const recChunksRef = useRef<Blob[]>([])
  const recTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const recCancelledRef = useRef(false)
  const fingerprintRef = useRef('')
  const initialScrollRef = useRef(false)

  const scrollToBottom = useCallback(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [])

  /* ── Linked group ──────────────────────────────────────────────────── */
  const loadLink = useCallback(() => {
    apiGet<{ jid: string | null; name: string | null }>(`/crm/deals/${dealId}/wa-group`)
      .then(setLink)
      .catch(() => setLink({ jid: null, name: null }))
  }, [dealId])
  useEffect(() => { loadLink() }, [loadLink])

  // Live-group picker when not linked (same list as Conversation History).
  useEffect(() => {
    if (!link || link.jid) return
    getWaGroups(search || undefined)
      .then((r) => { setGroups(r.groups); setGatewayUp(r.available) })
      .catch(() => { setGroups([]); setGatewayUp(false) })
  }, [link, search])

  /* ── Group header info ─────────────────────────────────────────────── */
  useEffect(() => {
    setPictureUrl(null)
    setParticipants(null)
    if (!link?.jid || !sessionId) return
    apiGet<any>(`/integrations/whatsapp/groups/${encodeURIComponent(link.jid)}/picture`, { sessionId })
      .then((r) => { const url = r?.data?.url || r?.url; if (url) setPictureUrl(url) })
      .catch(() => {})
    apiGet<any>(`/integrations/whatsapp/groups/${encodeURIComponent(link.jid)}`, { sessionId })
      .then((r) => {
        const info = r?.data || r
        if (Array.isArray(info?.participants)) setParticipants(info.participants.length)
      })
      .catch(() => {})
  }, [link?.jid, sessionId])

  /* ── Messages: fetch + poll + socket ───────────────────────────────── */
  const fetchMessages = useCallback(async (isPolling = false) => {
    if (!link?.jid) return
    try {
      if (!isPolling) setLoadingMsgs(true)
      const res = await apiGet<any>(`/integrations/whatsapp/groups/${encodeURIComponent(link.jid)}/messages`, { limit: 200 })
      const raw = res?.data || res
      const msgs: GroupMessage[] = Array.isArray(raw) ? raw : Array.isArray(raw?.messages) ? raw.messages : []
      // Reaction changes alter metadata, not ids — include them so polling picks them up.
      const fp = msgs.map((m) => `${m.id}:${(metaOf(m).reactions || []).length}`).join('|')
      if (fp !== fingerprintRef.current) {
        const oldCount = fingerprintRef.current ? fingerprintRef.current.split('|').length : 0
        fingerprintRef.current = fp
        setMessages(msgs)
        if (isPolling && msgs.length > oldCount) setTimeout(scrollToBottom, 50)
      }
    } catch { /* keep whatever is shown */ } finally {
      if (!isPolling) setLoadingMsgs(false)
    }
  }, [link?.jid, scrollToBottom])

  useEffect(() => {
    fingerprintRef.current = ''
    initialScrollRef.current = false
    setMessages([])
    if (!link?.jid) return
    fetchMessages()
    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = setInterval(() => fetchMessages(true), 5000)
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [link?.jid, fetchMessages])

  useEffect(() => {
    if (messages.length > 0 && !initialScrollRef.current) { initialScrollRef.current = true; scrollToBottom() }
  }, [messages, scrollToBottom])

  useEffect(() => {
    if (!socket || !link?.jid) return
    const onMsg = (payload: any) => {
      if (payload.groupId !== link.jid || !payload.message) return
      setMessages((prev) => (prev.some((m) => m.id === payload.message.id) ? prev : [...prev, payload.message]))
      setTimeout(scrollToBottom, 50)
    }
    const onReaction = (payload: any) => {
      if (payload.groupId !== link.jid || !payload.messageId) return
      setMessages((prev) => prev.map((m) => {
        if (m.id !== payload.messageId) return m
        const meta = metaOf(m)
        return { ...m, metadata: { ...meta, reactions: payload.reactions || [] } }
      }))
    }
    socket.on('whatsapp_group_message', onMsg)
    socket.on('whatsapp_group_reaction', onReaction)
    return () => { socket.off('whatsapp_group_message', onMsg); socket.off('whatsapp_group_reaction', onReaction) }
  }, [socket, link?.jid, scrollToBottom])

  useEffect(() => {
    const close = () => setMenu(null)
    if (menu) window.addEventListener('click', close)
    return () => window.removeEventListener('click', close)
  }, [menu])

  /* ── Send ──────────────────────────────────────────────────────────── */
  const send = async () => {
    if (!link?.jid || !sessionId || sending) return
    if (!text.trim() && !pendingFile) return
    setSending(true)
    try {
      if (pendingFile) {
        let type = 'document'
        if (pendingFile.type.startsWith('image/')) type = 'image'
        else if (pendingFile.type.startsWith('video/')) type = 'video'
        else if (pendingFile.type.startsWith('audio/')) type = 'audio'
        await apiPost(`/integrations/whatsapp/groups/${encodeURIComponent(link.jid)}/send`, {
          sessionId, type, base64: pendingFile.base64, mimetype: pendingFile.type, filename: pendingFile.name,
          caption: text.trim() || undefined, replyToId: replyTo?.id || undefined,
        })
        setPendingFile(null)
      } else {
        await apiPost(`/integrations/whatsapp/groups/${encodeURIComponent(link.jid)}/send`, {
          sessionId, text: text.trim(), replyToId: replyTo?.id || undefined,
        })
      }
      setText('')
      setReplyTo(null)
      setTimeout(async () => { await fetchMessages(true); setTimeout(scrollToBottom, 100) }, 800)
    } catch {
      crmToast('Could not send — check the WhatsApp connection', 'error')
    } finally { setSending(false) }
  }

  /* ── Voice notes ───────────────────────────────────────────────────── */
  const startRecording = async () => {
    if (!link?.jid || !sessionId || recording) return
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mime = ['audio/ogg;codecs=opus', 'audio/webm;codecs=opus', 'audio/mp4'].find((m) => MediaRecorder.isTypeSupported(m)) || ''
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined)
      recChunksRef.current = []
      recCancelledRef.current = false
      rec.ondataavailable = (e) => { if (e.data.size > 0) recChunksRef.current.push(e.data) }
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop())
        if (recTimerRef.current) clearInterval(recTimerRef.current)
        setRecording(false)
        setRecSecs(0)
        if (recCancelledRef.current || recChunksRef.current.length === 0) return
        const blob = new Blob(recChunksRef.current, { type: rec.mimeType || 'audio/webm' })
        const base64 = await new Promise<string>((resolve) => {
          const r = new FileReader()
          r.onload = () => resolve(String(r.result).split(',')[1] || '')
          r.readAsDataURL(blob)
        })
        try {
          setSending(true)
          await apiPost(`/integrations/whatsapp/groups/${encodeURIComponent(link.jid!)}/send`, {
            sessionId, type: 'audio', base64, mimetype: blob.type, ptt: true,
          })
          setTimeout(async () => { await fetchMessages(true); setTimeout(scrollToBottom, 100) }, 800)
        } catch { crmToast('Could not send the voice note', 'error') } finally { setSending(false) }
      }
      recRef.current = rec
      rec.start()
      setRecording(true)
      setRecSecs(0)
      recTimerRef.current = setInterval(() => setRecSecs((s) => s + 1), 1000)
    } catch { crmToast('Microphone access denied', 'error') }
  }
  const stopRecording = (cancel: boolean) => {
    recCancelledRef.current = cancel
    recRef.current?.stop()
  }
  useEffect(() => () => { if (recRef.current && recRef.current.state !== 'inactive') { recCancelledRef.current = true; recRef.current.stop() } }, [])

  /* ── Attach ────────────────────────────────────────────────────────── */
  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const r = new FileReader()
    r.onload = () => {
      const full = String(r.result)
      setPendingFile({
        name: file.name, size: file.size, type: file.type || 'application/octet-stream',
        preview: file.type.startsWith('image/') ? full : undefined,
        base64: full.split(',')[1] || full,
      })
    }
    r.readAsDataURL(file)
    e.target.value = ''
  }

  const toggleStar = async (msg: GroupMessage) => {
    if (!link?.jid) return
    try {
      const res = await apiPost<any>(`/integrations/whatsapp/groups/${encodeURIComponent(link.jid)}/messages/${encodeURIComponent(msg.id)}/star`, {})
      const starred = res?.isStarred ?? res?.starred
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, isStarred: starred } : m)))
    } catch { /* ignore */ }
    setMenu(null)
  }

  /* ── Render: link picker ───────────────────────────────────────────── */
  if (!link) return <p className="text-sm text-txt-tertiary">Loading…</p>
  if (!link.jid) {
    return (
      <div className="max-w-[560px]">
        <p className="text-sm text-txt-secondary mb-1">Link {dealName ? `${dealName}’s` : 'this lead’s'} WhatsApp group to chat with them directly from here — these are the LIVE groups on the connected WhatsApp number. The same link powers the Conversation History summary.</p>
        <SearchInput value={search} onChange={setSearch} placeholder="Search live WhatsApp groups…" className="my-2" />
        <div className="rounded-xl border border-border divide-y divide-border max-h-72 overflow-y-auto">
          {groups === null ? (
            <p className="text-[12px] text-txt-tertiary p-3">Loading live groups…</p>
          ) : !gatewayUp ? (
            <p className="text-[12px] text-amber-700 p-3">WhatsApp gateway not reachable — check the connected number in Settings → WhatsApp.</p>
          ) : groups.length === 0 ? (
            <p className="text-[12px] text-txt-tertiary p-3">No live groups match.</p>
          ) : (
            groups.map((g) => (
              <button
                key={g.jid}
                onClick={() =>
                  linkWaGroup(dealId, g.jid, g.name)
                    .then(() => { crmToast(`Linked “${g.name}”`, 'success'); loadLink() })
                    .catch(() => crmToast('Could not link the group', 'error'))
                }
                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-surface-hover"
              >
                <span className="min-w-0">
                  <span className="block text-[13px] font-semibold text-txt-primary truncate">{g.name}</span>
                  <span className="block text-[11px] text-txt-tertiary truncate">{g.jid}</span>
                </span>
                <span className="text-[11px] font-bold text-brand-600 shrink-0">Link →</span>
              </button>
            ))
          )}
        </div>
      </div>
    )
  }

  /* ── Render: chat ──────────────────────────────────────────────────── */
  const groupName = link.name || 'WhatsApp group'
  return (
    <div className="flex flex-col rounded-xl border border-border overflow-hidden" style={{ height: 'min(640px, 70vh)' }}>
      {/* Header — WhatsApp style */}
      <div className="flex items-center gap-3 px-3.5 py-2.5 shrink-0" style={{ backgroundColor: '#f0f2f5' }}>
        <div className="relative w-10 h-10 rounded-full shrink-0 overflow-hidden">
          {pictureUrl ? (
            <img src={pictureUrl} alt="" className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: avatarColor(groupName) }}>
              <span className="text-sm font-bold text-white">{initials(groupName)}</span>
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold truncate" style={{ color: '#111b21' }}>{groupName}</p>
          <p className="text-[11.5px] truncate" style={{ color: '#667781' }}>
            {waStatus.activeSession ? (participants != null ? `${participants} participants · live` : 'live') : 'WhatsApp disconnected — check Settings → WhatsApp'}
          </p>
        </div>
        <a
          href={`/groups/${encodeURIComponent(groupName)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border border-border bg-surface text-txt-primary hover:bg-surface-hover shrink-0"
          title="Open this group on the support Groups page"
        >
          Open in Groups
        </a>
        <button
          onClick={() => {
            linkWaGroup(dealId, null, null).then(() => { crmToast('Group unlinked', 'success'); loadLink() }).catch(() => crmToast('Could not unlink', 'error'))
          }}
          className="px-2.5 py-1.5 rounded-lg text-[11px] text-txt-tertiary border border-border bg-surface hover:text-txt-primary shrink-0"
        >
          Change group
        </button>
      </div>

      {/* Messages — WhatsApp wallpaper */}
      <div className="flex-1 overflow-y-auto px-4 py-3" style={{ backgroundColor: '#efeae2' }}>
        {loadingMsgs ? (
          <p className="text-center text-[12px] py-8" style={{ color: '#667781' }}>Loading conversation…</p>
        ) : messages.length === 0 ? (
          <p className="text-center text-[12px] py-8" style={{ color: '#667781' }}>No messages stored yet — new messages in the group appear here live.</p>
        ) : (
          messages.map((msg, i) => {
            const prev = i > 0 ? messages[i - 1] : null
            const isOut = msg.direction === 'OUTGOING'
            const showDate = !prev || fmtDate(prev.timestamp) !== fmtDate(msg.timestamp)
            const showSender = !isOut && (showDate || !prev || prev.from !== msg.from || prev.direction === 'OUTGOING')
            const sender = isOut ? 'You' : msg.senderName && !msg.senderName.includes('@g.us') ? msg.senderName : (msg.from || '').replace(/@.*$/, '')
            const meta = metaOf(msg)
            const qBody = msg.replyBody || meta.replyBody || null
            const qSender = msg.replySenderName || meta.replySenderName || null
            const qType = msg.replyType || meta.replyType || null
            const isImg = !!msg.mediaUrl && (msg.type === 'image' || /\.(jpg|jpeg|png|gif|webp)$/i.test(msg.mediaUrl))
            const isVideo = !!msg.mediaUrl && msg.type === 'video'
            const isAudio = !!msg.mediaUrl && (['audio', 'ptt'].includes(msg.type) || /\.(ogg|opus|oga|mp3|m4a|aac|wav|webm)$/i.test(msg.mediaUrl))
            const isDoc = !!msg.mediaUrl && !isImg && !isVideo && !isAudio
            return (
              <React.Fragment key={msg.id}>
                {showDate && (
                  <div className="flex justify-center my-3">
                    <span className="px-3 py-1 rounded-lg text-[11px] font-medium shadow-sm" style={{ backgroundColor: '#ffffff', color: '#54656f' }}>{fmtDate(msg.timestamp)}</span>
                  </div>
                )}
                <div className={`flex items-start gap-2 mb-1 ${isOut ? 'justify-end' : ''}`}>
                  <div className="relative max-w-[75%]" onMouseEnter={() => setHoveredId(msg.id)} onMouseLeave={() => setHoveredId(null)}>
                    <div
                      id={`dwamsg-${msg.id}`}
                      className="relative rounded-lg px-2.5 py-1.5 shadow-sm transition-all duration-300"
                      style={{ backgroundColor: isOut ? '#d9fdd3' : '#ffffff', borderTopLeftRadius: !isOut && showSender ? 0 : undefined }}
                      onContextMenu={(e) => { e.preventDefault(); setMenu({ x: Math.min(e.clientX, window.innerWidth - 180), y: Math.min(e.clientY, window.innerHeight - 160), msg }) }}
                    >
                      {hoveredId === msg.id && (
                        <button
                          className="z-20 rounded-full p-0.5"
                          style={{ position: 'absolute', top: 4, right: 4, backgroundColor: isOut ? '#d9fdd3' : '#ffffff' }}
                          onClick={(e) => { e.stopPropagation(); const r = (e.currentTarget as HTMLElement).getBoundingClientRect(); setMenu({ x: Math.min(r.right + 4, window.innerWidth - 180), y: Math.min(r.top, window.innerHeight - 160), msg }) }}
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="#8696a0" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                        </button>
                      )}
                      {msg.isStarred && (
                        <span className="absolute -top-1.5 -left-1.5 text-amber-400 z-10" title="Starred">
                          <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
                        </span>
                      )}
                      {msg.replyToId && qBody && (
                        <div
                          className="rounded-md px-2.5 py-1.5 mb-1 border-l-4 border-green-500 cursor-pointer"
                          style={{ backgroundColor: isOut ? '#c8edd0' : '#f0f0f0' }}
                          onClick={() => {
                            const el = document.getElementById(`dwamsg-${msg.replyToId}`)
                            if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.classList.add('ring-2', 'ring-green-400'); setTimeout(() => el.classList.remove('ring-2', 'ring-green-400'), 2000) }
                          }}
                        >
                          <p className="text-[11px] font-semibold text-green-700">{qSender || 'Unknown'}</p>
                          <p className="text-[12px] text-gray-600 truncate">{qType && qType !== 'chat' && qType !== 'text' ? `[${qType}]` : qBody}</p>
                        </div>
                      )}
                      {showSender && (
                        <p className="text-[13px] font-semibold mb-0.5" style={{ color: senderColor(msg.from || ''), lineHeight: 1.3 }}>{sender}</p>
                      )}
                      {isImg && (
                        <img src={msg.mediaUrl} alt={msg.body || 'Image'} className="max-w-full rounded-md mb-1 cursor-pointer hover:opacity-90" style={{ maxHeight: 300 }} onClick={() => window.open(msg.mediaUrl, '_blank')} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }} />
                      )}
                      {isVideo && <video src={msg.mediaUrl} controls className="max-w-full rounded-md mb-1" style={{ maxHeight: 300 }} />}
                      {isAudio && <audio src={msg.mediaUrl} controls preload="metadata" className="mb-1" style={{ maxWidth: 260, height: 40 }} />}
                      {isDoc && (() => {
                        const docName = meta.docFilename || msg.body || msg.mediaUrl?.split('/').pop() || 'Document'
                        const ext = (docName.split('.').pop() || '').toLowerCase()
                        const color = ext === 'pdf' ? '#E53935' : ['doc', 'docx'].includes(ext) ? '#1565C0' : ['xls', 'xlsx', 'csv'].includes(ext) ? '#2E7D32' : '#546E7A'
                        return (
                          <a href={msg.mediaUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 rounded-lg mb-1 px-2.5 py-2 hover:opacity-90" style={{ backgroundColor: 'rgba(0,0,0,0.05)', minWidth: 220, maxWidth: 320, textDecoration: 'none' }}>
                            <span className="shrink-0 w-8 h-9 rounded flex items-center justify-center text-[9px] font-bold text-white" style={{ backgroundColor: color }}>{ext.toUpperCase() || 'FILE'}</span>
                            <span className="text-[12.5px] truncate" style={{ color: '#111b21' }}>{docName}</span>
                          </a>
                        )
                      })()}
                      {msg.body && msg.body !== `[${msg.type}]` && !(isDoc && (meta.docFilename === msg.body || msg.body === '[document]')) && (
                        <p className="text-[14.2px] whitespace-pre-wrap break-words" style={{ color: '#111b21', lineHeight: 1.35 }}>{linkify(msg.body)}</p>
                      )}
                      {!msg.body && !msg.mediaUrl && msg.type !== 'chat' && <p className="text-sm italic" style={{ color: '#8696a0' }}>[{msg.type}]</p>}
                      <p className="text-[11px] mt-0.5 text-right" style={{ color: isOut ? '#1b7e4e' : '#667781' }}>{fmtTime(msg.timestamp)}</p>
                      {(() => {
                        const rx: { who: string; emoji: string }[] = Array.isArray(meta.reactions) ? meta.reactions : []
                        if (!rx.length) return null
                        const grouped: Record<string, string[]> = {}
                        rx.forEach((r) => { (grouped[r.emoji] = grouped[r.emoji] || []).push(r.who) })
                        return (
                          <div className={`flex gap-1 mt-1 ${isOut ? 'justify-end' : ''}`}>
                            {Object.entries(grouped).map(([e, whos]) => (
                              <span key={e} className="px-1.5 py-px rounded-full text-[11px] shadow-sm border" style={{ backgroundColor: '#f7f8fa', borderColor: '#e5e7eb', color: '#54656f' }} title={whos.join(', ')}>
                                {e}{whos.length > 1 ? ` ${whos.length}` : ''}
                              </span>
                            ))}
                          </div>
                        )
                      })()}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            )
          })
        )}
        <div ref={endRef} />
      </div>

      {/* Reply strip */}
      {replyTo && (
        <div className="flex items-center gap-2 px-3 py-2 border-t border-border" style={{ backgroundColor: '#f0f2f5' }}>
          <div className="flex-1 rounded-md px-2.5 py-1.5 border-l-4 border-green-500 bg-white min-w-0">
            <p className="text-[11px] font-semibold text-green-700">{replyTo.direction === 'OUTGOING' ? 'You' : replyTo.senderName || 'Unknown'}</p>
            <p className="text-[12px] text-gray-600 truncate">{replyTo.body || `[${replyTo.type}]`}</p>
          </div>
          <button onClick={() => setReplyTo(null)} className="text-txt-tertiary hover:text-txt-primary px-1 text-lg leading-none">×</button>
        </div>
      )}

      {/* Pending attachment strip */}
      {pendingFile && (
        <div className="flex items-center gap-3 px-3 py-2 border-t border-border" style={{ backgroundColor: '#f0f2f5' }}>
          {pendingFile.preview ? (
            <img src={pendingFile.preview} alt="" className="w-12 h-12 rounded object-cover" />
          ) : (
            <span className="w-10 h-11 rounded flex items-center justify-center text-[9px] font-bold text-white bg-slate-500 shrink-0">{(pendingFile.name.split('.').pop() || 'FILE').toUpperCase().slice(0, 4)}</span>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-[12.5px] font-semibold truncate" style={{ color: '#111b21' }}>{pendingFile.name}</p>
            <p className="text-[11px]" style={{ color: '#667781' }}>{pendingFile.size > 1024 * 1024 ? `${(pendingFile.size / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(pendingFile.size / 1024)} KB`} · sends with your message</p>
          </div>
          <button onClick={() => setPendingFile(null)} className="text-txt-tertiary hover:text-txt-primary px-1 text-lg leading-none">×</button>
        </div>
      )}

      {/* Composer */}
      <div className="flex items-end gap-2 px-3 py-2.5 shrink-0" style={{ backgroundColor: '#f0f2f5' }}>
        <input ref={fileRef} type="file" className="hidden" onChange={onFile} />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={!sessionId || recording}
          className="p-2 rounded-full hover:bg-black/5 disabled:opacity-40 shrink-0"
          title="Attach image / video / document"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="#54656f" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 119.5 7.372L1.153 15.72" /></svg>
        </button>
        {recording ? (
          <div className="flex-1 flex items-center gap-3 px-3 py-2 rounded-lg bg-white">
            <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
            <span className="text-[13px] font-semibold" style={{ color: '#111b21' }}>Recording… {Math.floor(recSecs / 60)}:{String(recSecs % 60).padStart(2, '0')}</span>
            <span className="flex-1" />
            <button onClick={() => stopRecording(true)} className="text-[12px] font-semibold text-red-600 hover:underline">Cancel</button>
          </div>
        ) : (
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() } }}
            placeholder={sessionId ? (pendingFile ? 'Add a caption…' : 'Type a message') : 'WhatsApp disconnected'}
            disabled={!sessionId}
            rows={1}
            className="flex-1 resize-none rounded-lg bg-white px-3 py-2 text-[14px] focus:outline-none disabled:opacity-50"
            style={{ color: '#111b21', maxHeight: 120 }}
          />
        )}
        {recording ? (
          <button onClick={() => stopRecording(false)} className="p-2.5 rounded-full shrink-0" style={{ backgroundColor: '#25D366' }} title="Send voice note">
            <svg className="w-5 h-5" fill="#fff" viewBox="0 0 24 24"><path d="M3.4 20.4l17.45-7.48a1 1 0 000-1.84L3.4 3.6a.993.993 0 00-1.39.91L2 9.12c0 .5.37.93.87.99L17 12 2.87 13.88c-.5.07-.87.5-.87 1l.01 4.61c0 .71.73 1.2 1.39.91z" /></svg>
          </button>
        ) : text.trim() || pendingFile ? (
          <button onClick={send} disabled={sending || !sessionId} className="p-2.5 rounded-full shrink-0 disabled:opacity-50" style={{ backgroundColor: '#25D366' }} title="Send">
            <svg className="w-5 h-5" fill="#fff" viewBox="0 0 24 24"><path d="M3.4 20.4l17.45-7.48a1 1 0 000-1.84L3.4 3.6a.993.993 0 00-1.39.91L2 9.12c0 .5.37.93.87.99L17 12 2.87 13.88c-.5.07-.87.5-.87 1l.01 4.61c0 .71.73 1.2 1.39.91z" /></svg>
          </button>
        ) : (
          <button onClick={startRecording} disabled={!sessionId || sending} className="p-2 rounded-full hover:bg-black/5 disabled:opacity-40 shrink-0" title="Record a voice note">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="#54656f" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" /></svg>
          </button>
        )}
      </div>

      {/* Message context menu */}
      {menu && (
        <div className="fixed z-50 rounded-lg shadow-xl border border-border bg-white py-1 min-w-[150px]" style={{ left: menu.x, top: menu.y }}>
          <button onClick={() => { setReplyTo(menu.msg); setMenu(null) }} className="w-full text-left px-3.5 py-1.5 text-[13px] hover:bg-gray-100" style={{ color: '#111b21' }}>Reply</button>
          <button onClick={() => toggleStar(menu.msg)} className="w-full text-left px-3.5 py-1.5 text-[13px] hover:bg-gray-100" style={{ color: '#111b21' }}>{menu.msg.isStarred ? 'Unstar' : 'Star'}</button>
          {menu.msg.body && (
            <button onClick={() => { navigator.clipboard?.writeText(menu.msg.body || ''); setMenu(null) }} className="w-full text-left px-3.5 py-1.5 text-[13px] hover:bg-gray-100" style={{ color: '#111b21' }}>Copy text</button>
          )}
        </div>
      )}
    </div>
  )
}
